import {
  OwnerShortfall,
  ownerShortfallMessage,
  ownerShortfallShortMessage,
} from '@lib/utils/ownerAffordability';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Tooltip from '@mui/material/Tooltip';
import classNames from 'classnames';
import css from './BilledToOwnerNotice.module.scss';

/**
 * Shown on every Add Media surface when the target library is billed to another account. It
 * accompanies the cost estimate rather than replacing it: what the media costs is true whoever
 * pays, so the estimate stays and this explains whose credits it will come out of — which is
 * also why the balance-relative figures ("remaining after", "/ available", percentages) are
 * absent next to it. Kept in one place (like `CONTENT_RIGHTS_NOTICE`) so the selection footers,
 * the two confirm screens and the upload-box impact popup can't drift apart on the wording.
 *
 * Every string below is a function of the owner's name because the name is optional
 * *independently of the condition that shows the notice*, not because two variants were wanted.
 * Whether to show it is the billing question (`resolveBillingOwnership`); who to name is the
 * ownership one (`formatOwnerName`), and that one can come up empty on a notice that is
 * correctly displayed — an owner the backend gave no name and no email, or a library the reader
 * owns that is billed elsewhere. The nameless fallback is the sentence this notice shipped with
 * when a billing id was all we had. Naming the owner is the better sentence — "their credits"
 * beats "the library owner's credits", and the reader can go ask that person — so the fallback
 * is the exception, not the default.
 */
export const billedToOwnerNotice = (ownerName?: string) =>
  ownerName
    ? `This library belongs to ${ownerName}. Adding media here uses their credits and storage — not yours.`
    : "This library is billed to another account. Adding media here uses the library owner's credits and storage — not yours.";

/** Compact form for the selection footers, where the full sentence won't fit. */
export const billedToOwnerShort = (ownerName?: string) =>
  ownerName ? `Billed to ${ownerName}, not you` : "Billed to the library's owner, not you";

/**
 * Post-failure form, for the upload panel's billing-failure row. The backend charged the
 * library's owner and they came up short, so the fix isn't on this user's side — saying so is
 * the whole point, since the default row offers a top-up of the uploader's own wallet. Naming
 * the owner matters most here: it turns "you can't fix this" into "ask this person".
 */
export const billedToOwnerFailureNotice = (ownerName?: string) =>
  ownerName
    ? `This library belongs to ${ownerName}, so it's their credits or storage that ran out — topping up your own account won't let these files through.`
    : "This library is billed to another account, so it's the library owner's credits or storage that ran out — topping up your own account won't let these files through.";

type BilledToOwnerNoticeProps = {
  /**
   * `badge` matches the pill-shaped items in the selection footers; `block` is the full-width
   * row used on the confirm screens, the upload box and the impact popup.
   */
  variant?: 'badge' | 'block';
  /**
   * The owner's display name, when we have one. Undefined falls back to the nameless wording —
   * it must not suppress the notice, which is about *whose* money and stays true unnamed. The
   * caller decides whether to render this component at all, from the billing axis.
   */
  ownerName?: string;
  /**
   * Escalates the notice in place rather than adding a second row: the shortfall sentence already
   * carries the "not your money" fact this component exists to state. Callers pass the verdict,
   * not the words — five surfaces render this and none should pick their own sentence.
   */
  shortfall?: OwnerShortfall;
  /**
   * Overrides the sentence entirely, for callers that build their own from the exported
   * builders above (the upload panel's failure row). `block` only, and it outranks `shortfall`.
   */
  message?: string;
  className?: string;
};

/** The glyph carries the escalation too, since `.danger` only changes hue. */
const NoticeIcon = ({ shortfall }: { shortfall?: OwnerShortfall }) =>
  shortfall ? (
    <ErrorOutlineRoundedIcon className={css.icon} />
  ) : (
    <InfoOutlinedIcon className={css.icon} />
  );

export const BilledToOwnerNotice = ({
  variant = 'block',
  ownerName,
  shortfall,
  message,
  className,
}: BilledToOwnerNoticeProps) => {
  const full =
    message ??
    (shortfall ? ownerShortfallMessage(shortfall, ownerName) : billedToOwnerNotice(ownerName));

  if (variant === 'badge') {
    // The badge text is abbreviated, so the full sentence has to be reachable — and a bare
    // `title` on a plain div isn't: there's nothing to focus, so a keyboard user can never
    // surface it and screen readers don't reliably announce it. A `Tooltip` around a focusable
    // element opens on hover *and* focus, and `aria-label` carries the sentence regardless.
    return (
      <Tooltip title={full} placement="top" arrow>
        <div
          className={classNames(css.notice, css.badge, { [css.danger]: !!shortfall }, className)}
          tabIndex={0}
          role="note"
          aria-label={full}
        >
          <NoticeIcon shortfall={shortfall} />
          <span aria-hidden="true">
            {shortfall
              ? ownerShortfallShortMessage(shortfall, ownerName)
              : billedToOwnerShort(ownerName)}
          </span>
        </div>
      </Tooltip>
    );
  }

  return (
    <div
      className={classNames(css.notice, css.block, { [css.danger]: !!shortfall }, className)}
      role="note"
    >
      <NoticeIcon shortfall={shortfall} />
      <span>{full}</span>
    </div>
  );
};
